export type Params = Record<string, Array<string> | string | undefined>;

export const PAGINATION_PARAMS = {
  offset: "0",
  limit: "25",
};

export const DS_PAGINATION_PARAMS = {
  offset: 0,
  limit: 25,
};

export type PaginationParams = typeof PAGINATION_PARAMS;

type ValidateArray<Value extends string> = {
  val: unknown;
  defaultVal?: Array<Value>;
  validValues?: Array<Value>;
};

export function validateArray<Value extends string>({
  val,
  defaultVal = [],
  validValues = [],
}: ValidateArray<Value>): Array<Value> {
  const parsedArray = Array.isArray(val)
    ? (val as Array<Value>)
    : typeof val === "string"
      ? [val]
      : defaultVal;
  if (validValues.length === 0) {
    return parsedArray as Array<Value>;
  }
  return parsedArray.filter((value) =>
    validValues.includes(value as Value),
  ) as Array<Value>;
}

export function validateLevels<Value extends string>({
  val,
  defaultVal,
  validValues = [],
}: ValidateArray<Value>): Array<Value> | undefined {
  const parsedArray = Array.isArray(val)
    ? (val as Array<Value>)
    : typeof val === "string"
      ? [val]
      : defaultVal;

  if (!parsedArray) {
    return parsedArray;
  }

  if (validValues.length === 0) {
    return parsedArray as Array<Value>;
  }

  return parsedArray.filter((value) =>
    validValues.includes(value as Value),
  ) as Array<Value>;
}

type ValidatePreset<Preset extends string> = {
  val: unknown;
  defaultVal: Preset;
  validPresets: Preset[];
};

export function validatePreset<Preset extends string>({
  val,
  defaultVal,
  validPresets,
}: ValidatePreset<Preset>): Preset {
  if (typeof val === "string") {
    return validPresets.includes(val as Preset) ? (val as Preset) : defaultVal;
  }
  return defaultVal;
}

type ValidateRequiredString = { val: unknown; defaultVal?: string };

export function validateRequiredString({
  val,
  defaultVal = "",
}: ValidateRequiredString): string {
  if (typeof val === "string") {
    return val;
  } else return defaultVal;
}

type ValidateOptionalString = { val: unknown };

export function validateOptionalString({
  val,
}: ValidateOptionalString): string | undefined {
  if (typeof val === "string") {
    return val;
  } else {
    return undefined;
  }
}

type ValidateLimit = { val: unknown; defaultVal?: string };
export function validateLimit({
  val,
  defaultVal = PAGINATION_PARAMS.limit,
}: ValidateLimit): string {
  if (typeof val === "number") {
    return val.toString();
  } else if (typeof val === "string") {
    const parsed = parseInt(val, 10);
    return isNaN(parsed) ? defaultVal : parsed.toString();
  }
  return defaultVal;
}

type ValidateOffset = { val: unknown; defaultVal?: string };
export function validateOffset({
  val,
  defaultVal = PAGINATION_PARAMS.offset,
}: ValidateOffset): string {
  if (typeof val === "number") {
    return val.toString();
  } else if (typeof val === "string") {
    const parsed = parseInt(val, 10);
    return isNaN(parsed) ? defaultVal : parsed.toString();
  }
  return defaultVal;
}

type ValidateDsLimit = { val: unknown; defaultVal?: number };
export function validateDsLimit({
  val,
  defaultVal = DS_PAGINATION_PARAMS.limit,
}: ValidateDsLimit): number {
  if (typeof val === "number") {
    return val;
  } else if (typeof val === "string") {
    const parsed = parseInt(val, 10);
    return isNaN(parsed) ? defaultVal : parsed;
  }
  return defaultVal;
}

type ValidateDsOffset = { val: unknown; defaultVal?: number };
export function validateDsOffset({
  val,
  defaultVal = DS_PAGINATION_PARAMS.offset,
}: ValidateDsOffset): number {
  if (typeof val === "number") {
    return val;
  } else if (typeof val === "string") {
    const parsed = parseInt(val, 10);
    return isNaN(parsed) ? defaultVal : parsed;
  }
  return defaultVal;
}

function serializeArrays([key, value]: [
  string,
  Array<string> | string | undefined,
]): Array<string | undefined> {
  if (Array.isArray(value)) {
    const newValue = value.join(",");
    return [key, newValue];
  } else {
    return [key, value];
  }
}

export function encodeSearchParams<QueryParams extends Record<string, string>>(
  params: Params,
): QueryParams {
  const encodedEntries = Object.entries(params)
    .map(serializeArrays)
    .filter(([, value]) => value);

  return Object.fromEntries(encodedEntries);
}
